import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { AuthLoadingFallback } from '@/features/auth/components/AuthLoadingFallback';
import { legalConfig } from '@/shared/config/legal';

type AppSurface = 'traveler' | 'guide' | 'admin';

interface RoleSurfaceGateProps {
  surface: AppSurface;
  children: ReactNode;
}

const PUBLIC_LEGAL_PATHS: Set<string> = new Set([
  legalConfig.privacyPolicyPath,
  legalConfig.termsPath,
]);

export function RoleSurfaceGate({ surface, children }: RoleSurfaceGateProps) {
  const { accountKind, isAuthenticated, isInitializing } = useAuth();
  const location = useLocation();

  // Public pages that must remain readable without role-based redirects.
  if (
    PUBLIC_LEGAL_PATHS.has(location.pathname) ||
    location.pathname.startsWith('/g/')
  ) {
    return <>{children}</>;
  }

  if (isInitializing) {
    return <AuthLoadingFallback />;
  }

  if (!isAuthenticated) {
    return <>{children}</>;
  }

  if (surface === 'traveler') {
    if (accountKind === 'super_admin') {
      return <Navigate to="/admin/dashboard" replace />;
    }
    if (accountKind === 'approved_guide') {
      return <Navigate to="/dashboard" replace />;
    }
  }

  if (surface === 'guide' && accountKind === 'super_admin') {
    return <Navigate to="/admin/dashboard" replace />;
  }

  if (surface === 'admin' && accountKind !== 'super_admin') {
    if (accountKind === 'approved_guide') {
      return <Navigate to="/dashboard" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
